import React, { useEffect } from 'react'
import { object, string } from 'yup'
import { Grid } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'

import {
  Form,
  FormSchema,
  FormSchemaMessages,
  FormSchemaTests,
  InputNormalizers,
  RadioGroupControl,
  SubmitButton,
  TextFieldControl,
} from '../../../../components/form'
import { Dialog, useSnackbarContext } from '../../../../components'
import { useMutateRegisterB2B } from '../../../../services'
import { omitRequestParams, preventHandler } from '../../../../utils.ts'
import { NewB2BFormState, QrFormProps } from '../../qrs.types.ts'
import { RegisterB2BQr } from '../../../../types.ts'

import { UipField } from '../form-fields/uip-field.tsx'
import { TotalTaxField } from '../form-fields/total-tax-field.tsx'
import { MerchantField } from '../form-fields/merchant-field.tsx'
import { useAppSelector } from '@/stores/hooks.ts'

const newQrValidationSchema = object<FormSchema<NewB2BFormState>>({
  merchantId: string().required(FormSchemaMessages.required),
  purpose: string().required(FormSchemaMessages.required),
  reference: string(),
  amount: string().when(['reusable'], ([reusable], schema) =>
    reusable === 'true'
      ? schema
      : schema.required(FormSchemaMessages.required).test(FormSchemaTests.isWithinRange(10, 1000000))
  ),
  reusable: string().required(FormSchemaMessages.required),
  takeTax: string().required(FormSchemaMessages.required),
  totalTaxAmount: string().when(['takeTax', 'amount', 'reusable'], ([takeTax, amount, reusable], schema) => {
    let enhancedSchema = schema.test(FormSchemaTests.isLessOrEqual(amount))

    // для многоразовой (статики) можно не заполнять сумму НДС, для одноразовой (динамики) - нужно
    if (takeTax === 'true' && reusable === 'false') {
      enhancedSchema = enhancedSchema.required(FormSchemaMessages.required)
    }

    return enhancedSchema
  }),
  expireMin: string().test(FormSchemaTests.isWithinRange(5, 129600)),
  uip: string().test(FormSchemaTests.isNotMaxLength(35)),
})

const QR_FORM_NAME = 'newQrForm'

export const NewB2BForm = ({ open, onClose }: QrFormProps): React.ReactElement => {
  const tspList = useAppSelector((state) => state.user.user?.tspList ?? [])
  const qc = useQueryClient()
  const { mutateAsync, status, isPending, data, reset } = useMutateRegisterB2B()
  const { addSnack } = useSnackbarContext()

  const handleB2BQrSubmit = async ({ takeTax, reusable, amount, totalTaxAmount, ...formState }: NewB2BFormState) => {
    await preventHandler(
      mutateAsync,
      isPending
    )(
      omitRequestParams<RegisterB2BQr>({
        ...formState,
        reusable: reusable === 'true',
        takeTax: takeTax === 'true',
        amount: Number(amount),
        totalTaxAmount: Number(totalTaxAmount),
      })
    )
  }

  useEffect(() => {
    switch (status) {
      case 'success': {
        const response = data?.data
        const isSuccess = response.state === 'REGISTERED'
        const errorMessage =
          response.state === 'REJECTED'
            ? response?.errorMessage ?? 'Произошла ошибка при создании QR, попробуйте ещё раз'
            : ''

        addSnack({
          message: isSuccess ? 'Заявка на создание QR B2B успешно отправлена' : errorMessage,
          severity: isSuccess ? 'success' : 'error',
          timeout: isSuccess ? 6000 : 3000,
          closeIcon: false,
        })

        if (isSuccess) {
          qc.invalidateQueries({ queryKey: ['B2B_list'] })
          onClose()
          reset()
        }

        break
      }

      case 'error': {
        addSnack({
          message: 'Произошла непредвиденная ошибка, попробуйте ещё раз',
          severity: 'error',
          timeout: 3000,
          closeIcon: false,
        })
        break
      }

      default:
        break
    }
  }, [status])

  return (
    <Form<NewB2BFormState>
      name={QR_FORM_NAME}
      onSubmit={handleB2BQrSubmit}
      validationSchema={newQrValidationSchema}
      initialValues={{
        merchantId: tspList[0]?.merchantId,
        reusable: 'false',
        takeTax: 'true',
      }}
    >
      <Dialog
        title="Заявка на B2B QR"
        maxWidth="md"
        open={open}
        onClose={onClose}
        content={
          <Grid container flexDirection="column" spacing={2}>
            <Grid item xs={12}>
              <MerchantField />
            </Grid>
            <Grid item xs={12}>
              <TextFieldControl<NewB2BFormState> name="reference" fullWidth label="№ заказа в магазине" />
            </Grid>
            <Grid item xs={12}>
              <TextFieldControl<NewB2BFormState>
                name="amount"
                fullWidth
                normalize={InputNormalizers.positiveNumbersOnly()}
                label="Сумма платежа"
                revalidateFields={['totalTaxAmount']}
              />
            </Grid>
            <Grid item xs={12}>
              <TextFieldControl<NewB2BFormState> name="purpose" fullWidth label="Назначение платежа" />
            </Grid>
            <Grid item xs={12}>
              <RadioGroupControl<NewB2BFormState>
                name="takeTax"
                revalidateFields={['totalTaxAmount']}
                row
                label="НДС"
                items={[
                  { title: 'С НДС', value: 'true' },
                  { title: 'Без НДС', value: 'false' },
                ]}
              />
            </Grid>
            <TotalTaxField />
            <Grid item xs={12}>
              <RadioGroupControl<NewB2BFormState>
                row
                items={[
                  { title: 'Статика', value: 'true' },
                  { title: 'Динамика', value: 'false' },
                ]}
                label="Тип ссылки"
                name="reusable"
                revalidateFields={['amount', 'uip', 'totalTaxAmount']}
              />
            </Grid>
            <UipField />
            <Grid item xs={12}>
              <TextFieldControl<NewB2BFormState>
                name="expireMin"
                fullWidth
                label="Время жизни ссылки (мин)"
                helperText="Введите значение от 5 до 129600 (90 дней). Время по умолчанию: 4320 (трое суток)"
              />
            </Grid>
          </Grid>
        }
        actions={<SubmitButton form={QR_FORM_NAME}>Отправить заявку</SubmitButton>}
      />
    </Form>
  )
}
